// ED-RK-004: what a screen needs to know about a read to tell "it failed" from "it answered with nothing". Any TanStack
// query result fits (useRows, useRow, useOfflineRows, a screen's own useQuery); no imports, so UI code can use it freely.

export type ReadState = { isLoading: boolean; isError: boolean; isFetching: boolean; data: unknown; refetch: () => unknown };

/** A read that failed and holds nothing to show (a re-read that failed after a good one keeps showing the good rows). */
export function readFailed(q: Pick<ReadState, "isError" | "data">): boolean {
  return q.isError && q.data === undefined;
}

/** A heading with the count of its rows, once they are read: "Invoices (3)"; with no rows read, the heading alone, never "(0)". */
export function withCount(title: string, rows: readonly unknown[] | undefined): string {
  return rows ? `${title} (${rows.length})` : title;
}

/**
 * Several reads that together make one figure or list, as one: loading while any is loading, failed while any failed (a
 * total built from some of its reads is not the total), and Try again reads the failed ones again.
 */
export function combineReads(reads: ReadState[]): ReadState {
  const failed = reads.filter(readFailed);
  return {
    isLoading: reads.some((r) => r.isLoading),
    isError: failed.length > 0,
    isFetching: reads.some((r) => r.isFetching),
    data: failed.length > 0 ? undefined : true,
    refetch: () => Promise.all(failed.map((r) => r.refetch()))
  };
}
